import * as React from "react"
import { gql } from "@apollo/client"
import { Button, Center, Flex, SimpleGrid, Spinner, Stack, Text, useDisclosure } from "@chakra-ui/react"
import Head from "next/head"

import { paginate } from "lib/apollo/helpers"
import { PostType, useGetPostsQuery } from "lib/graphql"
import { useLocalStorage } from "lib/hooks/useLocalStorage"
import { useMe } from "lib/hooks/useMe"
import { Layout } from "components/Layout"
import { Limiter } from "components/Limiter"
import { LoginForm } from "components/LoginForm"
import { Modal } from "components/Modal"
import PostCreateForm from "components/PostCreateForm"
import { PostFilters, PostsFilterbar } from "components/PostsFilterbar"
import { PostTile } from "components/PostTile"
import dayjs from "dayjs"

const _ = gql`
  query GetPosts($take: Int, $orderBy: [PostOrderByWithRelationInput!], $where: PostWhereInput, $skip: Int) {
    posts(take: $take, orderBy: $orderBy, where: $where, skip: $skip) {
      items {
        ...PostItem
      }
      count
    }
  }
`

const TAKE = 9
export default function Posts() {
  const { me } = useMe()
  const loginModalProps = useDisclosure()
  const modalProps = useDisclosure()

  const handleGoToLogin = () => {
    loginModalProps.onOpen()
  }
  const [filters, setFilters] = useLocalStorage<PostFilters>("kunstbende.filters.posts", {
    type: "",
    region: "",
    category: "",
  })
  const { data, loading, fetchMore, refetch } = useGetPostsQuery({
    variables: {
      take: TAKE,
      where: {
        deadline: { gte: dayjs().format("YYYY-MM-DD") }, // make sure to use this format otherwise fetchMore query will fail
        isClosed: { equals: false },
        type: { equals: (filters.type as PostType) || undefined },
        region: { equals: filters.region || undefined },
        categories: filters.category ? { has: filters.category } : undefined,
      },
    },
  })

  const handleFetchMore = () => {
    if (!posts) return
    return fetchMore({
      variables: { skip: posts.length, take: TAKE },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updateQuery: paginate("posts"),
    })
  }

  const posts = data?.posts.items
  const postCount = data?.posts.count

  return (
    <>
      <Head>
        <title>Kunstbende - Prikbord</title>
      </Head>

      <Limiter>
        <Stack w="100%" mb={{ base: 40, lg: 40 }} spacing={{ base: 4, md: 4 }} mt={16}>
          <Flex justify="space-between" align="center">
            <Text
              fontSize={{ base: 32, md: 56 }}
              fontWeight={900}
              lineHeight={{ base: "shorter", md: "short" }}
              px={0}
              textAlign="left"
              role="heading"
              aria-level={1}
            >
              Prikbord
            </Text>
            {me ? (
              <Button colorScheme="yellow" onClick={modalProps.onOpen}>
                Oproep maken
              </Button>
            ) : (
              <Button colorScheme="yellow" variant="outline" onClick={handleGoToLogin}>
                Login om een oproep te plaatsen
              </Button>
            )}
          </Flex>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            <PostsFilterbar filters={filters} onSetFilters={setFilters} />
          </SimpleGrid>

          {loading ? (
            <Center h={300}>
              <Spinner />
            </Center>
          ) : !!!posts ? (
            <Center h={300}>
              <Text fontWeight="bold" fontSize="2xl">
                Fout bij ophalen van berichten
              </Text>
            </Center>
          ) : posts.length === 0 ? (
            <Center h={300}>
              <Text fontWeight="bold" fontSize="2xl">
                Geen berichten gevonden
              </Text>
            </Center>
          ) : (
            <Stack pt={8} role="main" aria-label="Prikbord overzicht" spacing={8}>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
                {posts.map((post) => (
                  <PostTile key={post.id} post={post} />
                ))}
              </SimpleGrid>

              {postCount && posts.length < postCount ? (
                <Center>
                  <Button onClick={handleFetchMore} colorScheme="yellow">
                    Meer laden
                  </Button>
                </Center>
              ) : null}
            </Stack>
          )}
        </Stack>
        <Modal title="Plaats een oproep op het prikbord" {...modalProps}>
          <PostCreateForm refetch={refetch} />
        </Modal>
        <Modal {...loginModalProps}>
          <LoginForm handleSubmit={loginModalProps.onClose} />
        </Modal>
      </Limiter>
    </>
  )
}

Posts.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>
